import React from 'react'
import Markdown from 'react-markdown'
import type { Plugin } from 'unified'
import type { Root, Text, PhrasingContent } from 'mdast'
import { visit } from 'unist-util-visit'
import { Emoji } from '../../../services/api'

interface EmojiMarkdownProps {
  children: string
  emojis: Map<string, Emoji>
}

const EMOJI_REGEX = /:([^\s:]+):/g

/**
 * Remark plugin that replaces :emoji-name: patterns with inline image nodes.
 */
const remarkCustomEmoji = (emojis: Map<string, Emoji>): Plugin<[], Root> => {
  return () => (tree) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (!parent || index === undefined) return

      const value = node.value
      EMOJI_REGEX.lastIndex = 0

      const parts: PhrasingContent[] = []
      let lastIndex = 0
      let match: RegExpExecArray | null

      while ((match = EMOJI_REGEX.exec(value)) !== null) {
        const emojiName = match[1]
        const emoji = emojis.get(emojiName)
        if (!emoji) continue

        // Text before the emoji
        if (match.index > lastIndex) {
          parts.push({ type: 'text', value: value.slice(lastIndex, match.index) })
        }

        // Inline emoji image
        parts.push({
          type: 'image',
          url: emoji.image_url,
          alt: `:${emojiName}:`,
          title: `:${emojiName}:`,
          data: {
            hProperties: {
              className:
                '!inline !h-[1.2em] !w-[1.2em] !m-0 !p-0 align-[-0.2em] object-contain',
            },
          },
        } as any)

        lastIndex = match.index + match[0].length
      }

      if (parts.length === 0) return

      // Remaining text
      if (lastIndex < value.length) {
        parts.push({ type: 'text', value: value.slice(lastIndex) })
      }

      // Replace the text node with the new parts
      parent.children.splice(index, 1, ...parts)
    })
  }
}

/**
 * Markdown renderer that also replaces :emoji-name: patterns with inline images
 * using a remark plugin for AST-level transformation.
 */
export const EmojiMarkdown: React.FC<EmojiMarkdownProps> = ({ children, emojis }) => {
  const remarkPlugins = React.useMemo(
    () => (emojis.size > 0 ? [remarkCustomEmoji(emojis)] : []),
    [emojis]
  )

  return (
    <Markdown remarkPlugins={remarkPlugins}>
      {children}
    </Markdown>
  )
}
